import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
    OrderDto,
    ReceiptDto,
    OrderStatus,
    ReceiptType,
    OrderGoodsItemDto,
    ChangeOrderStatusDto,
} from '../../api/ClientApi';
import { WarehouseClient, approveOrder, receiveOrder } from '../../api/Extensions';
import { myAssert } from '../../common/console';
import { RootState } from '../reducers';
import { addOrUpdateOrder, addOrderGoodsItemToOrder, removeOrderGoodsItemFromOrder } from './orderActions';
import { addOrUpdateMultipleReduxGoods } from './goodsActions';
import { filterUniqueBareGoodsByArticle } from './helper';
import { addOrUpdateDemandOrder } from './demandOrderActions';
import { addOrUpdateReturnOrder } from './returnOrderActions';
import { IndexedValidation } from '../reducers/common';
import { updateReduxSite } from './siteActions';
import { addOrUpdatePurchaseOrder } from './purchaseOrderActions';
import { importApprovedOrderGoodsItems } from './orderGoodsItemsActions';

export const createNewEmptyTransferReceipt = createAction('receipt/createNewEmptyTransferReceipt');

export const createNewEmptyReceptionReceipt = createAction('receipt/createNewEmptyReceptionReceipt');

export const createNewEmptyDeliveryReceipt = createAction('receipt/createNewEmptyDeliveryReceipt');

export const createNewEmptyReturnReceipt = createAction('receipt/createNewEmptyReturnReceipt');

export const createNewReceipt = createAction<ReceiptDto>('receipt/createNewReceipt');

export const clearReceipt = createAction('receipt/clearReceipt');

export const importReceipt = createAsyncThunk<ReceiptDto, number>(
    'receipt/importReceipt',
    async (payload, thunkAPI) => {
        return WarehouseClient.getReceipt(payload, true);
    },
);

export const updateReceiptExecutionDate = createAction<Date | undefined>('receipt/updateReceiptExecutionDate');

export const updateReceiptData = createAction<string>('receipt/updateReceiptData');

export const updateReceiptDescription = createAction<string>('receipt/updateReceiptDescription');
export const updateReceptionBatchId = createAction<string>('receipt/updateReceptionBatchId');

export const setOrderedGoodsValid = createAction<IndexedValidation[]>('receipt/setOrderedGoodsValid');
export const changeOrderedGoodsValid = createAction<IndexedValidation>('receipt/changeOrderedGoodsValid');

export const saveTransferReceipt = createAsyncThunk<ReceiptDto, number>(
    'receipt/saveTransferReceipt',
    async (payload: number, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const receiptState = rootState.receiptForm;

        const order = rootState.orderForm.order ? new OrderDto(rootState.orderForm.order) : null;
        const bareGoods = rootState.goodsToOrderForm.bareGoodsEntities;

        const receipt = receiptState.receipt;

        myAssert(() => receipt !== null);

        if (!order) {
            throw new Error();
        }

        const orderId: number | undefined = order.id;

        const newOrder = new OrderDto(order);

        const filteredBareGoods = filterUniqueBareGoodsByArticle(bareGoods ?? []);

        if (receipt === null || !orderId || orderId <= 0) {
            throw new Error();
        }

        const newReceipt = new ReceiptDto(receipt);

        if (receipt.receiptType === ReceiptType.Reception) {
            throw new Error();
        }

        newReceipt.orderId = orderId;
        newReceipt.order = order;

        myAssert(() => newOrder.id !== undefined);

        if (newOrder.id === undefined) {
            throw new Error();
        }

        const filteredOrderGoodsItems = newOrder.orderGoodsItems;

        let savedOrderGoodsItems: OrderGoodsItemDto[] = [];
        let removedGoods: number[] = [];

        const savedOrderGoodsItemsPromises: Promise<OrderGoodsItemDto>[] = [];
        const removedOrderGoodsItemsIdsPromises: Promise<number>[] = [];

        // go through oldGoods;
        filteredOrderGoodsItems?.map((fg) => {
            // check if this goods exists in new order
            const orderedGoods = filteredBareGoods?.find((bg) => bg.article?.id === fg.article?.id);

            if (!!orderedGoods) {
                // if quantity is different, update existing goods quantity
                if (orderedGoods.orderedQuantity !== fg.quantity) {
                    fg.quantity = orderedGoods.orderedQuantity;

                    const addOrderGoodsItemPromise = WarehouseClient.addNewOrderGoodsItemForOrder(orderId, fg).then(
                        (res) => {
                            thunkAPI.dispatch(addOrderGoodsItemToOrder({ orderId, orderGoodsItem: res }));
                            return res;
                        },
                    );

                    savedOrderGoodsItemsPromises.push(addOrderGoodsItemPromise);
                }
            } else {
                // this goods does not exist in new order, so we have to delete it and disconnect it from the order
                if (newOrder.orderStatus === OrderStatus.Pending) {
                    const removedOrderGoodsItemsIdsPromise = WarehouseClient.deleteOrderGoodsItem(fg.id ?? 0).then(
                        () => {
                            thunkAPI.dispatch(
                                removeOrderGoodsItemFromOrder({
                                    orderId: newOrder.id ?? 0,
                                    orderGoodsItemId: fg.id,
                                }),
                            );
                            return fg.id ?? 0;
                        },
                    );
                    removedOrderGoodsItemsIdsPromises.push(removedOrderGoodsItemsIdsPromise);
                }
            }
        });

        filteredBareGoods
            .filter((fbg) => !filteredOrderGoodsItems?.find((fg) => fg.article?.id === fbg.article?.id))
            .map((fbg) => {
                // in this case, goods does not exist in old order, so we have to add id and connect it to the order
                const newOrderGood = new OrderGoodsItemDto();
                newOrderGood.articleId = fbg.article?.id;
                newOrderGood.article = fbg.article;
                newOrderGood.order = undefined;
                newOrderGood.orderId = undefined;
                newOrderGood.quantity = fbg.orderedQuantity;

                const addOrderGoodsItemPromise = WarehouseClient.addNewOrderGoodsItemForOrder(
                    orderId,
                    newOrderGood,
                ).then((res) => {
                    thunkAPI.dispatch(addOrderGoodsItemToOrder({ orderId, orderGoodsItem: res }));
                    return res;
                });

                savedOrderGoodsItemsPromises.push(addOrderGoodsItemPromise);
            });

        savedOrderGoodsItems = await Promise.all(savedOrderGoodsItemsPromises);

        removedGoods = await Promise.all(removedOrderGoodsItemsIdsPromises);

        newOrder.orderGoodsItems = newOrder.orderGoodsItems?.filter(
            (ogi) => !savedOrderGoodsItems.find((sogi) => sogi.id === ogi.id),
        );
        newOrder.orderGoodsItems?.push(...savedOrderGoodsItems);
        newOrder.orderGoodsItems = newOrder.orderGoodsItems?.filter((ogi) => !removedGoods.includes(ogi.id ?? 0));

        if (!!newOrder.demandOrderId) {
            WarehouseClient.getDemandOrder(newOrder.demandOrderId).then((res) =>
                thunkAPI.dispatch(addOrUpdateDemandOrder(res)),
            );
        }

        if (!!newOrder.returnOrderId) {
            WarehouseClient.getReturnOrder(newOrder.returnOrderId).then((res) =>
                thunkAPI.dispatch(addOrUpdateReturnOrder(res)),
            );
        }

        // One backend transaction: transfer receipt + status Approved.
        newReceipt.order = undefined;

        const step = await approveOrder(orderId, newReceipt);

        thunkAPI.dispatch(addOrUpdateOrder(step.order));
        thunkAPI.dispatch(importApprovedOrderGoodsItems());

        const savedReceipt =
            step.order.receipts?.find((r) => r.receiptType === ReceiptType.Transfer) ??
            step.order.receipts?.[step.order.receipts.length - 1] ??
            newReceipt;

        return savedReceipt;
    },
    {
        condition: (payload: number, thunkAPI: { getState: any }) => {
            const receiptState = (thunkAPI.getState() as RootState).receiptForm;

            return receiptState.receipt !== null;
        },
    },
);

export const saveReceptionReceipt = createAsyncThunk<ReceiptDto, number>(
    'receipt/saveReceptionReceipt',
    async (payload: number, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const receiptState = rootState.receiptForm;

        const order = rootState.orderForm.order ? new OrderDto(rootState.orderForm.order) : null;

        const receipt = receiptState.receipt;
        const receptionBatchId = receiptState.receptionBatchId;

        myAssert(() => receipt !== null);

        if (!order) {
            throw new Error();
        }

        const orderId: number | undefined = order.id;

        if (receipt === null || !orderId || orderId <= 0) {
            throw new Error();
        }

        if (receipt.receiptType !== ReceiptType.Reception) {
            throw new Error();
        }

        order.purchaseOrder = undefined;
        order.supplier = undefined;
        order.demandOrder = undefined;
        order.returnOrder = undefined;
        order.destinationSite = undefined;
        order.originSite = undefined;
        order.goods = undefined;

        const newReceipt = new ReceiptDto(receipt);
        newReceipt.orderId = orderId;
        newReceipt.order = undefined;

        // One backend transaction: reception receipt + goods land on the destination + status Delivered.
        const step = await receiveOrder(orderId, newReceipt, receptionBatchId ?? undefined, receipt.executionDate);

        thunkAPI.dispatch(addOrUpdateMultipleReduxGoods(step.goods));
        thunkAPI.dispatch(addOrUpdateOrder(step.order));

        if (!!order.purchaseOrderId) {
            const resPurch = await WarehouseClient.getPurchaseOrder(order.purchaseOrderId);
            thunkAPI.dispatch(addOrUpdatePurchaseOrder(resPurch));
        }

        thunkAPI.dispatch(updateReduxSite(order.destinationSiteId ?? 0));

        const savedReceipt =
            step.order.receipts?.find((r) => r.receiptType === ReceiptType.Reception) ??
            step.order.receipts?.[step.order.receipts.length - 1] ??
            newReceipt;

        return savedReceipt;
    },
    {
        condition: (payload: number, thunkAPI: { getState: any }) => {
            const receiptState = (thunkAPI.getState() as RootState).receiptForm;

            return receiptState.receipt !== null;
        },
    },
);
